import * as events from "@configs/analytics/trackingEvents";
import { dispatchTrackedEvent } from "@configs/analytics/trackingEvents";
import useServerInfo from "@hooks/useServerInfo";
import { useMemo } from "react";

/**
 * Unified analytics tracking hook. All metadata-shaping logic lives in
 * `trackingEvents.ts`; this hook is a thin React adapter that injects the
 * shared dispatcher and (where needed) the server prefix used during
 * registration-complete identify.
 */
export function useAnalyticsTracking() {
  const serverInfo = useServerInfo();
  const serverPrefix = serverInfo?.prefix;

  return useMemo(() => {
    const track = dispatchTrackedEvent;
    return {
      // Core tracking
      track,

      // Registration tracking
      trackRegisterFormFocus: events.trackRegisterFormFocus.bind(null, track),
      trackRegisterClick: events.trackRegisterClick.bind(null, track),
      trackRegisterFormSubmit: events.trackRegisterFormSubmit.bind(null, track),
      trackOTPSent: events.trackOTPSent.bind(null, track),
      trackOTPVerified: events.trackOTPVerified.bind(null, track),
      trackRegistrationComplete: events.trackRegistrationComplete.bind(null, track),
      trackRegistrationFailed: events.trackRegistrationFailed.bind(null, track),
      trackOTPRequestFailed: events.trackOTPRequestFailed.bind(null, track),
      trackOTPFailedAttempt: events.trackOTPFailedAttempt.bind(null, track),
      trackOTPRequest: events.trackOTPRequest.bind(null, track),
      trackUsernameSuggestionShown: events.trackUsernameSuggestionShown.bind(null, track),
      trackUsernameSuggestionSelected: events.trackUsernameSuggestionSelected.bind(null, track),

      // General
      trackLoginClick: events.trackLoginClick.bind(null, track),
      trackGoogleAuthClick: events.trackGoogleAuthClick.bind(null, track),
      trackGooglePromptShown: () => events.trackGooglePromptShown(track),
      trackGooglePromptTap: () => events.trackGooglePromptTap(track),
      trackLogin: events.trackLogin.bind(null, track),
      trackLoginFormSubmit: events.trackLoginFormSubmit.bind(null, track),
      trackLoginFailed: events.trackLoginFailed.bind(null, track),
      trackAuthClick: events.trackAuthClick.bind(null, track),

      // Deposit tracking
      trackDepositClick: events.trackDepositClick.bind(null, track),
      trackDepositMethodGroup: events.trackDepositMethodGroup.bind(null, track),
      trackDepositMethodIndividual: events.trackDepositMethodIndividual.bind(null, track),
      trackAutoFastTriggered: events.trackAutoFastTriggered.bind(null, track),
      trackDepositAmountSelect: events.trackDepositAmountSelect.bind(null, track),
      trackDefaultDepositAmount: events.trackDefaultDepositAmount.bind(null, track),
      trackDepositAmountChanged: events.trackDepositAmountChanged.bind(null, track),
      trackTopUpSubmit: events.trackTopUpSubmit.bind(null, track),
      trackDepositSuccess: events.trackDepositSuccess.bind(null, track),
      trackDepositFailed: events.trackDepositFailed.bind(null, track),
      trackTopUpFailed: events.trackTopUpFailed.bind(null, track),
      trackDepositCreateSuccess: events.trackDepositCreateSuccess.bind(null, track),
      trackCloseDepositIframe: events.trackCloseDepositIframe.bind(null, track),
      trackOpenDepositIframe: events.trackOpenDepositIframe.bind(null, track),
      trackCloseDepositCustom: events.trackCloseDepositCustom.bind(null, track),
      trackOpenDepositCustom: events.trackOpenDepositCustom.bind(null, track),

      // PGW redirect auto-close tracking
      trackRedirectAutocloseResult: events.trackRedirectAutocloseResult.bind(null, track),

      // Gameplay tracking
      trackGameOpenTriggered: events.trackGameOpenTriggered.bind(null, track),
      trackGameSearch: events.trackGameSearch.bind(null, track),
      trackGameOpened: events.trackGameOpened.bind(null, track),
      trackGameClosed: events.trackGameClosed.bind(null, track),
      trackGameExit: events.trackGameExit.bind(null, track),
      trackGameError: events.trackGameError.bind(null, track),
      trackGameContentVisibilityFallbackClick: () => events.trackGameContentVisibilityFallbackClick(track),
      trackSessionTimeout: () => events.trackSessionTimeout(track),
      trackGameSearchSelect: events.trackGameSearchSelect.bind(null, track),

      // Surprise Me tracking
      trackSurpriseMeTileShown: () => events.trackSurpriseMeTileShown(track),
      trackSurpriseMeTileClicked: events.trackSurpriseMeTileClicked.bind(null, track),
      trackSurpriseMeNext: events.trackSurpriseMeNext.bind(null, track),
      trackSurpriseMeBack: events.trackSurpriseMeBack.bind(null, track),
      trackSurpriseMePlayNow: events.trackSurpriseMePlayNow.bind(null, track),
      trackSurpriseMeClosed: events.trackSurpriseMeClosed.bind(null, track),

      // Promotions tracking
      trackPromoClaimAttempt: events.trackPromoClaimAttempt.bind(null, track),
      trackPromoClaimSuccess: events.trackPromoClaimSuccess.bind(null, track),
      trackPromoIssued: events.trackPromoIssued.bind(null, track),
      trackPromoRewardApplied: events.trackPromoRewardApplied.bind(null, track),
      trackPromoBonusExpired: events.trackPromoBonusExpired.bind(null, track),
      trackClickMiniBanner: events.trackClickMiniBanner.bind(null, track),
      trackClickHomeBanner: events.trackClickHomeBanner.bind(null, track),
      trackClickPromotionPage: events.trackClickPromotionPage.bind(null, track),
      trackClickPromotionItem: events.trackClickPromotionItem.bind(null, track),
      trackCashbackMiniPromptShown: events.trackCashbackMiniPromptShown.bind(null, track),
      trackPromoAutoClaimPopup: events.trackPromoAutoClaimPopup.bind(null, track),
      trackPromotionPresented: events.trackPromotionPresented.bind(null, track),
      trackPromotionAction: events.trackPromotionAction.bind(null, track),
      trackPromotionClaimResult: events.trackPromotionClaimResult.bind(null, track),
      trackPromotionIssued: events.trackPromotionIssued.bind(null, track),
      trackPromotionQueueEnded: events.trackPromotionQueueEnded.bind(null, track),
      trackNudgeModalPresented: events.trackNudgeModalPresented.bind(null, track),
      trackDepositGroupsEmpty: events.trackDepositGroupsEmpty.bind(null, track),
      trackDepositMethodsEmpty: events.trackDepositMethodsEmpty.bind(null, track),

      // Withdrawals tracking
      trackWithdrawalClick: events.trackWithdrawalClick.bind(null, track),
      trackWithdrawalSubmit: events.trackWithdrawalSubmit.bind(null, track),
      trackWithdrawalSuccess: events.trackWithdrawalSuccess.bind(null, track),
      trackWithdrawalRequestFailed: events.trackWithdrawalRequestFailed.bind(null, track),

      // Notifications tracking
      trackNotificationInAppPromptShown: events.trackNotificationInAppPromptShown.bind(null, track),
      trackNotificationInAppPromptResponse: events.trackNotificationInAppPromptResponse.bind(null, track),
      trackNotificationBrowserPromptShown: () => events.trackNotificationBrowserPromptShown(track),
      trackNotificationBrowserPromptResponse: events.trackNotificationBrowserPromptResponse.bind(null, track),
      trackNotificationSubscriptionStatus: events.trackNotificationSubscriptionStatus.bind(null, track),

      // Language
      trackChangeLanguage: events.trackChangeLanguage.bind(null, track),

      // Customer Support (CS)
      trackCsClick: events.trackCsClick.bind(null, track),
      trackSelectCsChannel: events.trackSelectCsChannel.bind(null, track),
      trackContactCsEntryShown: events.trackContactCsEntryShown.bind(null, track),

      // Header
      trackHeaderShown: events.trackHeaderShown.bind(null, track),

      // PWA install funnel
      trackPwaEntryShown: events.trackPwaEntryShown.bind(null, track),
      trackPwaInstallClick: events.trackPwaInstallClick.bind(null, track),
      trackPwaInstallSuccess: events.trackPwaInstallSuccess.bind(null, track),

      // Promotion user journey (KZ-6739 / TK-815)
      trackShareConfirmed: events.trackShareConfirmed.bind(null, track),
      trackReferBallsLastBallUsed: () => events.trackReferBallsLastBallUsed(track),
    };
  }, []);
}
